// XCard: one card in the character's style (Sunny index card, Margo dark cue card, Arjun grid paper, Tavo scorecard).
// Front: label + title (+ foot). Back (done): label, the buddy's face, their line (Kalam), a note, and a DONE stamp.
// v2 rules (SPEC §2): stamp is always "DONE"; foot defaults to '' (never the v1 jargon); labels are plain
// ("UP NEXT · 9:30 AM · 25 MIN", "DONE · 9:58 AM", "YOUR FIRST CARD · 2 MIN").
//
// Sizing: `w`/`h` are the design size. fit='width' (default) shrinks the card to its parent's width (keeps the
// aspect, every metric scales); fit='contain' also shrinks to the parent's height (parent needs a definite height,
// e.g. flex: 1; min-height: 0); fit='none' renders at exactly w x h.
import type { JSX } from 'preact';
import { useRef } from 'preact/hooks';
import type { CharId } from '@shared/types';
import { C } from '@shared/content/characters';
import { useSize } from '../lib/useSize';

export interface XCardProps {
  ch: CharId;
  face?: 'front' | 'back';
  w?: number;
  h?: number;
  fit?: 'width' | 'contain' | 'none';
  label?: string;
  title?: string;
  /** Title font size at design width. Margo's mono title is drawn at 80% of it (as in the prototype). */
  titleSize?: number;
  foot?: string;
  /** Back: the buddy's line. */
  line?: string;
  /** Back: small caps note under the line. */
  note?: string;
  stamp?: string;
  stampSub?: string;
  /** Show the stamp on the front too (mini stamped cards in collections). */
  stamped?: boolean;
  /** Back: face expression 0-5 (default 4). */
  e?: number;
  /** Rotation in degrees (default -1; 0 for minis). */
  tilt?: number;
  flat?: boolean;
  /** CSS animation for the stamp, e.g. 'xmStamp 650ms 600ms cubic-bezier(.2,.9,.3,1.2) both'. */
  stampAnim?: string;
  /** Tavo: filled diamonds in the innings row (default 1). */
  hits?: number;
  class?: string;
  style?: JSX.CSSProperties;
}

interface CardStyle {
  bg: string; bgi: (k: number, mini: boolean) => string; bgs: (k: number, mini: boolean) => string; border: string; rad: number; ink: string;
  bar: boolean; barBg?: string; barInk?: string; labelInk: string; titleFont: string; titleInk: string; titleCase: 'none' | 'uppercase';
  titleLead: number; footInk: string; stampInk: string; lineInk: string; noteInk: string; blend: 'multiply' | 'normal'; padL: (mini: boolean) => number;
}

const HAND = "'Kalam', cursive";
const MONO = "'Space Mono', monospace";

const T: Record<CharId, CardStyle> = {
  sunny: {
    bg: '#FFFEFA',
    bgi: (k, mini) => {
      const m = (mini ? 13 : 30) * k, L = (mini ? 15 : 32) * k;
      return `linear-gradient(90deg, transparent ${m}px, #E9A0A0 ${m}px, #E9A0A0 ${m + Math.max(1, k)}px, transparent ${m + Math.max(1, k)}px), repeating-linear-gradient(180deg, transparent 0, transparent ${L - Math.max(1, k)}px, #C9DAEE ${L - Math.max(1, k)}px, #C9DAEE ${L}px)`;
    },
    bgs: () => 'auto', border: 'none', rad: 5, ink: '#23211C', bar: false,
    labelInk: '#7A5A10', titleFont: HAND, titleInk: '#23211C', titleCase: 'none', titleLead: 1.15,
    footInk: '#5A5650', stampInk: '#B8322E', lineInk: '#23211C', noteInk: '#5A5650', blend: 'multiply',
    padL: (mini) => (mini ? 13 + 7 : 30 + 14),
  },
  margo: {
    bg: '#141312', bgi: () => 'none', bgs: () => 'auto', border: '1px solid #3A352D', rad: 10, ink: '#F2EEE6', bar: true,
    barBg: '#E0A43A', barInk: '#1A1206', labelInk: '#E0A43A',
    titleFont: MONO, titleInk: '#F2EEE6', titleCase: 'uppercase', titleLead: 1.3,
    footInk: '#E0A43A', stampInk: '#E0A43A', lineInk: '#F2EEE6', noteInk: '#CFC8BC', blend: 'normal',
    padL: (mini) => (mini ? 9 : 18),
  },
  arjun: {
    bg: '#F4EFE4', bgi: () => 'linear-gradient(#DED6C6 1px, transparent 1px), linear-gradient(90deg, #DED6C6 1px, transparent 1px)',
    bgs: (k, mini) => { const g = (mini ? 12 : 22) * k; return `${g}px ${g}px`; }, border: 'none', rad: 14, ink: '#1B1E2B', bar: false,
    labelInk: '#2E3F7A', titleFont: HAND, titleInk: '#2E3F7A', titleCase: 'none', titleLead: 1.12,
    footInk: '#3E4356', stampInk: '#2E3F7A', lineInk: '#2E3F7A', noteInk: '#3E4356', blend: 'multiply',
    padL: (mini) => (mini ? 9 : 18),
  },
  tavo: {
    bg: '#FBF8F0', bgi: () => 'none', bgs: () => 'auto', border: 'none', rad: 10, ink: '#22324A', bar: true,
    barBg: '#2F5D45', barInk: '#FFFFFF', labelInk: '#2F5D45',
    titleFont: HAND, titleInk: '#22324A', titleCase: 'none', titleLead: 1.15,
    footInk: '#A8440B', stampInk: '#2F5D45', lineInk: '#22324A', noteInk: '#3D4A42', blend: 'multiply',
    padL: (mini) => (mini ? 9 : 18),
  },
};

/** Width the stamp takes from the right edge of the back face (design px): its box + 14px offset + 6px gap. */
function stampReserve(stamp: string, sub: string): number {
  const main = stamp.length * 17 * 0.68 + 22 + 6;
  const small = sub ? sub.length * 10.5 * 0.66 + 22 + 6 : 0;
  return Math.max(main, small) + 14 + 6;
}

export function XCard(props: XCardProps) {
  const { w = 300, h = 190, fit = 'width' } = props;
  const wrap = useRef<HTMLDivElement>(null);
  const size = useSize(wrap, { w, h });
  let k = 1;
  if (fit === 'width' && size.w > 0) k = Math.min(1, size.w / w);
  if (fit === 'contain' && size.w > 0 && size.h > 0) k = Math.min(1, size.w / w, size.h / h);
  if (fit === 'none') return <CardBody {...props} k={1} />;
  const wrapStyle: JSX.CSSProperties = fit === 'contain'
    ? { width: '100%', height: '100%', minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }
    : { width: '100%', maxWidth: w, height: Math.round(h * k), flex: 'none' };
  return (
    <div ref={wrap} class={props.class} style={{ ...wrapStyle, ...props.style }}>
      <CardBody {...props} class={undefined} style={undefined} k={k} />
    </div>
  );
}

function CardBody(p: XCardProps & { k: number }) {
  const { ch, k } = p;
  const W0 = p.w ?? 300, H0 = p.h ?? 190;
  const t = T[ch];
  const mini = W0 < 200;
  const back = p.face === 'back';
  const front = !back;
  const mono = ch === 'margo';
  const r = (n: number) => Math.round(n * k * 10) / 10;
  const W = r(W0), H = r(H0);
  const asked = p.titleSize || 0;
  const titleSize = r(asked ? (mono && !mini ? Math.round(asked * 0.8) : asked) : mini ? (mono ? 10 : 13) : Math.round(W0 / (mono ? 13 : 10.5)));
  const fs0 = mini ? 0 : Math.round(Math.min(W0 * 0.34, H0 * 0.3));
  const fs = r(fs0);
  const e = Math.max(0, Math.min(5, p.e != null ? Math.round(p.e) : 4));
  const col = e % 3, row = Math.floor(e / 3);
  const label = p.label ?? '';
  const stamp = p.stamp ?? 'DONE';
  const stampSub = p.stampSub ?? '';
  const foot = p.foot ?? '';
  const padL = t.padL(mini);
  const labelSize = r(mini ? 8.5 : 11.5);
  const showStamp = back || !!p.stamped;
  const tilt = p.tilt != null ? p.tilt : mini ? 0 : -1;
  const innings = ch === 'tavo' && !mini && H0 >= 140 && front;
  // A short note stays centered (prototype); a long one moves left of the stamp.
  const noteClear = !p.note || p.note.length * 11.5 * 0.64 + 2 * (stampReserve(stamp, stampSub) - 18) <= W0 - padL - 18;
  const hits = p.hits ?? 1;

  const rootStyle: JSX.CSSProperties = {
    width: W, height: H, position: 'relative', overflow: 'hidden', borderRadius: r(t.rad), backgroundColor: t.bg,
    backgroundImage: t.bgi(k, mini), backgroundSize: t.bgs(k, mini), border: t.border, color: t.ink,
    boxShadow: p.flat ? 'none' : mini ? `0 ${r(6)}px ${r(14)}px rgba(0,0,0,0.35)` : `0 ${r(18)}px ${r(40)}px rgba(0,0,0,0.45)`,
    transform: tilt ? `rotate(${tilt}deg)` : undefined, display: 'flex', flexDirection: 'column', fontFamily: MONO, flex: 'none',
    ...p.style,
  };
  const labelP = (inline: boolean, extra?: JSX.CSSProperties) => (
    <p style={{
      margin: 0, fontFamily: MONO, fontSize: labelSize, lineHeight: 1.25, fontWeight: 700, letterSpacing: inline ? '0.06em' : '0.08em',
      color: t.labelInk, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', ...extra,
    }}>{label}</p>
  );

  return (
    <div class={p.class} style={rootStyle}>
      {t.bar && (
        <p style={{
          margin: 0, flex: 'none', padding: mini ? (label ? `${r(4)}px ${r(8)}px` : `${r(3)}px 0`) : `${r(10)}px ${r(16)}px`,
          background: t.barBg, color: t.barInk, fontFamily: MONO, fontSize: labelSize, lineHeight: 1.25, fontWeight: 700,
          letterSpacing: '0.08em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>{label}</p>
      )}
      {front && (
        <div style={{
          flex: 1, minHeight: 0, padding: mini ? `${r(8)}px ${r(8)}px ${r(8)}px ${r(padL)}px` : `${r(16)}px ${r(18)}px ${r(16)}px ${r(padL)}px`,
          display: 'flex', flexDirection: 'column', gap: r(mini ? 4 : 10),
        }}>
          {!t.bar && !mini && labelP(true)}
          {innings && (
            <div aria-hidden="true" style={{ display: 'grid', gridTemplateColumns: 'repeat(9, minmax(0, 1fr))', border: `${Math.max(1, r(1.5))}px solid #22324A`, flex: 'none' }}>
              {Array.from({ length: 9 }, (_, i) => (
                <span key={i} style={{ height: r(20), borderRight: i < 8 ? '1px solid #22324A' : 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ width: r(8), height: r(8), transform: 'rotate(45deg)', background: i < hits ? '#E46A1F' : 'transparent' }} />
                </span>
              ))}
            </div>
          )}
          <p style={{
            margin: 0, fontFamily: t.titleFont, fontSize: titleSize, lineHeight: t.titleLead, fontWeight: 700, color: t.titleInk,
            textTransform: t.titleCase, overflowWrap: 'anywhere',
          }}>{p.title ?? ''}</p>
          {!mini && foot !== '' && (
            <p style={{ margin: 'auto 0 0', fontFamily: MONO, fontSize: r(11), lineHeight: 1.4, fontWeight: 700, letterSpacing: '0.05em', color: t.footInk }}>{foot}</p>
          )}
        </div>
      )}
      {back && (
        <div style={{
          flex: 1, minHeight: 0, padding: mini ? `${r(8)}px` : `${r(16)}px ${r(18)}px ${r(18)}px ${r(padL)}px`,
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: r(12), textAlign: 'center',
        }}>
          {!t.bar && !mini && labelP(true, { alignSelf: 'stretch', textAlign: 'left', whiteSpace: 'normal' })}
          {!mini && (
            <div style={{ flex: 'none', width: fs, height: fs, borderRadius: '50%', overflow: 'hidden', position: 'relative', background: '#8E8A84', boxShadow: `0 0 0 ${r(3)}px ${t.stampInk}` }}>
              <img src={C[ch].assets.faces} alt={`${C[ch].name} reacting`} draggable={false} style={{
                position: 'absolute', maxWidth: 'none', width: r(Math.round(fs0 * 3.3)), height: r(Math.round(fs0 * 2.2)),
                left: -r(Math.round(col * 1.1 * fs0 + 0.05 * fs0)), top: -r(Math.round(row * 1.1 * fs0 + 0.05 * fs0)),
              }} />
            </div>
          )}
          <p style={{ margin: 0, fontFamily: HAND, fontSize: r(mini ? 12 : Math.round(W0 / 12.5)), lineHeight: 1.18, fontWeight: 700, color: t.lineInk }}>{p.line ?? ''}</p>
          {!mini && !!p.note && (
            // The note stays left of the stamp (bottom right) so long notes never run under it.
            <p style={{
              margin: 0, fontFamily: MONO, fontSize: r(11.5), lineHeight: 1.5, letterSpacing: '0.04em', textTransform: 'uppercase', color: t.noteInk,
              ...(noteClear ? {} : { alignSelf: 'flex-start', width: `calc(100% - ${r(stampReserve(stamp, stampSub) - 18)}px)` }), textAlign: 'center',
            }}>{p.note}</p>
          )}
        </div>
      )}
      {showStamp && (
        <div style={{
          position: 'absolute', right: r(mini ? 5 : 14), bottom: r(mini ? (stamp.length > 8 ? 9 : 6) : 16),
          padding: mini ? `${r(3)}px ${r(5)}px` : `${r(7)}px ${r(11)}px`, border: `${r(mini ? 2 : 3)}px solid ${t.stampInk}`, borderRadius: r(7),
          color: t.stampInk, textAlign: 'center', transform: 'rotate(-11deg)', opacity: 0.9, mixBlendMode: t.blend,
          animation: p.stampAnim || 'none',
        }}>
          <p style={{ margin: 0, fontFamily: MONO, fontSize: r(mini ? 9 : 17), lineHeight: 1.1, fontWeight: 700, letterSpacing: '0.08em', whiteSpace: 'nowrap' }}>{stamp}</p>
          {!mini && stampSub !== '' && (
            <p style={{ margin: `${r(3)}px 0 0`, fontFamily: MONO, fontSize: r(10.5), lineHeight: 1.1, fontWeight: 700, letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>{stampSub}</p>
          )}
        </div>
      )}
    </div>
  );
}
